import React, { useCallback, useMemo } from "react";
import { scopeProjectRef, scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useGitStatus } from "../../lib/gitStatusState";
import { selectProjectByRef, useStore } from "../../store";
import { selectThreadTerminalState, useTerminalStateStore } from "../../terminalStateStore";
import { useUiStateStore } from "../../uiStateStore";
import { useThreadSelectionStore } from "../../threadSelectionStore";
import { resolveThreadRowClassName, resolveThreadStatusPill } from "../Sidebar.logic";
import type { SidebarThreadRowProps } from "./SidebarThreadRow";
import {
  resolveThreadLeadIndicator,
  resolveDisplayedThreadBranch,
  terminalStatusFromRunningIds,
} from "./SidebarThreadRow.helpers";
import { isSessionActivelyRunning } from "../../lib/threadRuntimeState";
import { useSidebarThreadPullRequest } from "./SidebarThreadPullRequestBadge";
import { useSidebarThreadRename } from "./useSidebarThreadRename";

// ---------------------------------------------------------------------------
// useSidebarThreadRow — all state subscriptions and callbacks for SidebarThreadRow
// ---------------------------------------------------------------------------

export function useSidebarThreadRow(props: SidebarThreadRowProps) {
  const {
    thread,
    orderedProjectThreadKeys,
    isActive,
    confirmingArchiveThreadKey,
    setConfirmingArchiveThreadKey,
    confirmArchiveButtonRefs,
    renamingThreadKey,
    renamingTitle,
    setRenamingTitle,
    renamingCommittedRef,
    renamingInputRef,
    handleThreadClick,
    navigateToThread,
    handleMultiSelectContextMenu,
    handleThreadContextMenu,
    clearSelection,
    commitRename,
    cancelRename,
    attemptArchiveThread,
    attemptSettleThread,
    openPrLink,
  } = props;

  const threadRef = scopeThreadRef(thread.environmentId, thread.id);
  const threadKey = scopedThreadKey(threadRef);

  const lastVisitedAt = useUiStateStore((state) => state.threadLastVisitedAtById[threadKey]);
  const linkedPrUrl = useUiStateStore((state) => state.threadLinkedPrById[threadKey] ?? null);
  const isSelected = useThreadSelectionStore((state) => state.selectedThreadKeys.has(threadKey));
  const hasSelection = useThreadSelectionStore((state) => state.selectedThreadKeys.size > 0);
  const runningTerminalIds = useTerminalStateStore(
    (state) =>
      selectThreadTerminalState(state.terminalStateByThreadKey, threadRef).runningTerminalIds,
  );
  const threadProjectCwd = useStore(
    useMemo(
      () => (state: import("../../store").AppState) =>
        selectProjectByRef(state, scopeProjectRef(thread.environmentId, thread.projectId))?.cwd ??
        null,
      [thread.environmentId, thread.projectId],
    ),
  );
  const gitCwd = thread.worktreePath ?? threadProjectCwd ?? props.projectCwd;
  // t3code's gate, restored. Forge had narrowed it to `thread.branch != null`,
  // which is a dead end for the exact thread that needs the query most: a
  // thread with no recorded branch never asked what branch it was on, so it
  // could never find out. A worktree thread always has a branch worth reading
  // even when the record does not name one. `watchGitStatus` is refcount-
  // deduped per (environmentId, cwd), so threads sharing a project share one
  // subscription and widening this costs nothing on a busy sidebar.
  const gitStatus = useGitStatus({
    environmentId: thread.environmentId,
    cwd: thread.branch != null || thread.worktreePath !== null ? gitCwd : null,
  });

  // Derived state
  const isHighlighted = isActive || isSelected;
  const isThreadRunning = isSessionActivelyRunning(thread.session);
  const threadStatus = resolveThreadStatusPill({ thread: { ...thread, lastVisitedAt } });
  // The branch actually checked out in `gitCwd`. `thread.branch` is the value
  // frozen when the thread was provisioned, and the two disagreeing is what the
  // panel warns about.
  const currentGitBranch = gitStatus.data?.branch ?? null;
  const displayBranch = resolveDisplayedThreadBranch({
    threadBranch: thread.branch,
    currentGitBranch,
  });
  // The thread's own linked change request, with the newest state the Command cache holds, is
  // what the badge and the hover mini list draw; git status still drives the merged lead glyph.
  const {
    pr,
    prStatus,
    indicator: linkedPrIndicator,
    entries: threadPullRequestEntries,
  } = useSidebarThreadPullRequest(thread, displayBranch, gitStatus.data ?? null, linkedPrUrl);
  const leadIndicator = resolveThreadLeadIndicator({ threadStatus, prStatus });
  const terminalStatus = terminalStatusFromRunningIds(runningTerminalIds);
  // The status glyph only says "something is running"; the details panel names
  // how many, so the count travels alongside the indicator rather than being
  // re-derived from a store the panel would have to subscribe to itself.
  const runningTerminalCount = runningTerminalIds.length;
  const isConfirmingArchive = confirmingArchiveThreadKey === threadKey && !isThreadRunning;
  // The keyboard trigger is scoped to the meta slot (`group/sidebar-status-slot`
  // on the row's trailing `justify-end` div), not the row-wide
  // `group-focus-within` it used to be: the row root is
  // `<div role="button" tabIndex={0} />`, so any click on it satisfied
  // focus-within and left this label blanked with the archive control pinned
  // over it once the pointer moved away.
  const threadMetaClassName = isConfirmingArchive
    ? "pointer-events-none opacity-0"
    : !isThreadRunning
      ? "pointer-events-none transition-opacity duration-150 group-hover/menu-sub-item:opacity-0 group-has-[:focus-visible]/sidebar-status-slot:opacity-0"
      : "pointer-events-none";
  const rowClassName = `${resolveThreadRowClassName({ isActive, isSelected })} relative isolate`;

  // Callbacks
  const clearConfirmingArchive = useCallback(() => {
    setConfirmingArchiveThreadKey((current) => (current === threadKey ? null : current));
  }, [setConfirmingArchiveThreadKey, threadKey]);

  const handleMouseLeave = useCallback(() => clearConfirmingArchive(), [clearConfirmingArchive]);

  const handleBlurCapture = useCallback(
    (event: React.FocusEvent<HTMLLIElement>) => {
      const currentTarget = event.currentTarget;
      requestAnimationFrame(() => {
        if (currentTarget.contains(document.activeElement)) return;
        clearConfirmingArchive();
      });
    },
    [clearConfirmingArchive],
  );

  const handleRowClick = useCallback(
    (event: React.MouseEvent) => handleThreadClick(event, threadRef, orderedProjectThreadKeys),
    [handleThreadClick, orderedProjectThreadKeys, threadRef],
  );

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      navigateToThread(threadRef);
    },
    [navigateToThread, threadRef],
  );

  const handleRowContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (hasSelection && isSelected) {
        void handleMultiSelectContextMenu({ x: event.clientX, y: event.clientY });
        return;
      }
      if (hasSelection) clearSelection();
      void handleThreadContextMenu(threadRef, { x: event.clientX, y: event.clientY });
    },
    [
      clearSelection,
      handleMultiSelectContextMenu,
      handleThreadContextMenu,
      hasSelection,
      isSelected,
      threadRef,
    ],
  );

  const {
    handleRenameInputRef,
    handleRenameInputChange,
    handleRenameInputKeyDown,
    handleRenameInputBlur,
    handleRenameInputClick,
  } = useSidebarThreadRename({
    threadRef,
    renamingInputRef,
    renamingCommittedRef,
    setRenamingTitle,
    renamingTitle,
    commitRename,
    cancelRename,
    originalTitle: thread.title,
  });

  const handlePrClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!prStatus) return;
      openPrLink(event, prStatus.url);
    },
    [openPrLink, prStatus],
  );

  const handleConfirmArchiveRef = useCallback(
    (element: HTMLButtonElement | null) => {
      if (element) {
        confirmArchiveButtonRefs.current.set(threadKey, element);
      } else {
        confirmArchiveButtonRefs.current.delete(threadKey);
      }
    },
    [confirmArchiveButtonRefs, threadKey],
  );

  const stopPropagationOnPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => event.stopPropagation(),
    [],
  );

  const handleConfirmArchiveClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      clearConfirmingArchive();
      void attemptArchiveThread(threadRef, thread);
    },
    [attemptArchiveThread, clearConfirmingArchive, thread, threadRef],
  );

  const handleStartArchiveConfirmation = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setConfirmingArchiveThreadKey(threadKey);
      requestAnimationFrame(() => {
        confirmArchiveButtonRefs.current.get(threadKey)?.focus();
      });
    },
    [confirmArchiveButtonRefs, setConfirmingArchiveThreadKey, threadKey],
  );

  const handleArchiveImmediateClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      void attemptArchiveThread(threadRef, thread);
    },
    [attemptArchiveThread, thread, threadRef],
  );

  // Settle has no confirmation step, unlike Archive — it's a lightweight,
  // reversible toggle in t3code, and this matches that exactly.
  const handleSettleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      void attemptSettleThread(threadRef, thread);
    },
    [attemptSettleThread, thread, threadRef],
  );

  return {
    threadRef,
    threadKey,
    isHighlighted,
    // Selected and active are different surfaces, and a row can be both — the
    // Activity card resolves its own background and needs them apart.
    isSelected,
    isThreadRunning,
    threadStatus,
    // The raw PR backs the Activity card's `#123` badge, which shows open and
    // closed PRs too; `prStatus` deliberately narrows to merged-and-linked.
    pr,
    prStatus,
    linkedPrIndicator,
    linkedPullRequestBadge: {
      number: thread.linkedPullRequest?.number ?? null,
      url: thread.linkedPullRequest?.url ?? null,
    },
    threadPullRequestEntries,
    leadIndicator,
    terminalStatus,
    runningTerminalCount,
    currentGitBranch,
    displayBranch,
    isConfirmingArchive,
    threadMetaClassName,
    rowClassName,
    renamingThreadKey,
    handleMouseLeave,
    handleBlurCapture,
    handleRowClick,
    handleRowKeyDown,
    handleRowContextMenu,
    handlePrClick,
    handleRenameInputRef,
    handleRenameInputChange,
    handleRenameInputKeyDown,
    handleRenameInputBlur,
    handleRenameInputClick,
    handleConfirmArchiveRef,
    stopPropagationOnPointerDown,
    handleConfirmArchiveClick,
    handleStartArchiveConfirmation,
    handleArchiveImmediateClick,
    handleSettleClick,
  };
}
